import { useRef, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { EmptyWell, LiftStationRotatorPump } from 'src/components/SCADA';
import { LiftStationWidgetPopup } from '../LiftStationWidgetPopup';
import { WidgetWrapper } from '../WidgetWrapper';
import { liftStationWrapper, liveDataBox } from './style';
import { useAuthentication } from 'src/features/authentication/context';
import { ControllerElement } from '../ControllerElement';
import { WidgetType } from 'src/features/sites/types/types';
import { AssetType } from 'src/types/assets';
import { useLiveData } from './hooks';
import { useWaterLevelData } from 'src/features/sites/hooks';
import { useWebSockets } from 'src/contexts/webSocketsContext/websockets.context';
import { useToggle } from 'src/hooks/useToggle';
import { ControllersService } from 'src/services/controllers/controllers.service';
import { Controller as ControllerType } from 'src/types/controllers/controller';
import { TagResponse } from 'src/types/tags/tags';
import { AssetService } from 'src/services/assets/asset.service';
import { useResponsive } from 'src/hooks/useResponsive';
import { LiveTagsList } from './components/LiveTagsList';

type Props = {
  widgetId: string;
  title?: string;
  isDesktop: boolean;
  gatewayInputs: TagResponse[];
  connectedTo: string;
  position: {
    x: number;
    y: number;
  };
  widget: WidgetType;
  messages?: any;
  isMobile: boolean;
  toggleAddOrRemoveHandler: () => void;
};

export const LiftStationWidget = ({
  widgetId,
  title,
  isDesktop,
  gatewayInputs,
  connectedTo,
  position,
  widget,
  isMobile,
  toggleAddOrRemoveHandler,
}: Props) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const { state, onToggle } = useToggle();
  const { messages } = useWebSockets();
  const [controllerState, setControllerState] = useState(false);

  const [asset, setAsset] = useState<AssetType>();
  const isExtraSmallDevice = useResponsive('down', 'sm');

  const isControllerIncluded = widget?.connectedTo?.controller?.id;
  const leftPumpAssetId = widget?.connectedTo?.leftPump?.id;
  const rightPumpAssetId = widget?.connectedTo?.rightPump?.id;
  const thirdPumpAssetId = widget?.connectedTo?.thirdPump?.id;
  const fourthPumpAssetId = widget?.connectedTo?.fourthPump?.id;
  const isWetWell = widget?.connectedTo?.wetWell?.id;

  const { formttedTags, liveDataComplete } = useLiveData({
    selectedTagIds: widget?.connectedTo?.liveDataTags,
    gatewayInputs: gatewayInputs,
    messages: messages,
  });

  const {
    customerId: { value: customerId },
    siteId: { value: siteId },
  } = useAuthentication();

  const [controller, setController] = useState<ControllerType>({
    id: '',
    controllerName: '',
    controllerType: '',
    gatewayId: '',
  });

  useEffect(() => {
    if (!customerId || !siteId || !connectedTo || !isControllerIncluded) return;

    ControllersService.getById(customerId, siteId, isControllerIncluded).then((response) => {
      setController(response);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectedTo, customerId, siteId]);

  useEffect(() => {
    if (!customerId || !siteId || !isWetWell) return;
    AssetService.getAssetById({
      customerId: customerId,
      siteId: siteId,
      assetId: widget?.connectedTo?.wetWell?.id,
    })
      .then((data) => setAsset(data))
      .catch((error) => console.error(error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, siteId, widget.connectedTo, widget.widgetType]);

  const { waterLevel, height, alarms, unit } = useWaterLevelData({
    asset: asset!,
    customerId: customerId,
  });

  const isStateMappedToLeftPump = widget?.connectedTo?.leftPump?.assetProperties?.some(
    (item: any) => item.tagName === 'State'
  );
  const isStateMappedToRightPump = widget?.connectedTo?.rightPump?.assetProperties?.some(
    (item: any) => item.tagName === 'State'
  );
  const isStateMappedToThirdPump = widget?.connectedTo?.thirdPump?.assetProperties?.some(
    (item: any) => item.tagName === 'State'
  );
  const isStateMappedToFourthPump = widget?.connectedTo?.fourthPump?.assetProperties?.some(
    (item: any) => item.tagName === 'State'
  );

  const pumpIds = [leftPumpAssetId, rightPumpAssetId, thirdPumpAssetId, fourthPumpAssetId].filter(
    Boolean
  );
  const pumpCount = pumpIds.length;

  const isTriplexPump = pumpCount >= 3;
  const isQuadroplexPump = pumpCount === 4;

  return (
    <>
      <WidgetWrapper
        toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
        title={title ?? ''}
        isDataReady={true}
        isLiftStationWidget
        ref={wrapperRef}
        height={isDesktop ? undefined : 520}
        onEdit={onToggle}
        widgetId={widgetId}
      >
        <Box sx={liveDataBox}>
          <LiveTagsList
            formttedTags={formttedTags}
            liveDataComplete={liveDataComplete}
            isMobile={isMobile}
          />
        </Box>
        <Box sx={liftStationWrapper}>
          <Box>
            <EmptyWell
              asset={asset}
              heightWell={height}
              waterLevel={waterLevel}
              alarmData={alarms}
              unit={unit}
              widget={widget}
              isWetWell={isWetWell}
              isTriplexPump={isTriplexPump}
              isQuadroplexPump={isQuadroplexPump}
            >
              {leftPumpAssetId && (
                <LiftStationRotatorPump
                  siteId={siteId!}
                  customerId={customerId!}
                  assetId={leftPumpAssetId}
                  isStateMapped={isStateMappedToLeftPump}
                  messages={messages}
                  isTriplexPump={isTriplexPump}
                  isQuadroplexPump={isQuadroplexPump}
                />
              )}
              {thirdPumpAssetId && (
                <LiftStationRotatorPump
                  siteId={siteId!}
                  customerId={customerId!}
                  assetId={thirdPumpAssetId}
                  isStateMapped={isStateMappedToThirdPump}
                  messages={messages}
                  isTriplexPump={isTriplexPump}
                  isQuadroplexPump={isQuadroplexPump}
                />
              )}
              {fourthPumpAssetId && (
                <LiftStationRotatorPump
                  siteId={siteId!}
                  customerId={customerId!}
                  assetId={fourthPumpAssetId}
                  isStateMapped={isStateMappedToFourthPump}
                  messages={messages}
                  isTriplexPump={isTriplexPump}
                  isQuadroplexPump={isQuadroplexPump}
                />
              )}
              {rightPumpAssetId && (
                <LiftStationRotatorPump
                  siteId={siteId!}
                  customerId={customerId!}
                  assetId={rightPumpAssetId}
                  isStateMapped={isStateMappedToRightPump}
                  messages={messages}
                  isTriplexPump={isTriplexPump}
                  isQuadroplexPump={isQuadroplexPump}
                />
              )}
            </EmptyWell>
            {isControllerIncluded && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  marginTop: isTriplexPump ? (isExtraSmallDevice ? '10px' : '15px') : '12px',
                  // marginRight: isTriplexPump && !isExtraSmallDevice ? '-80px' : '0',
                  marginLeft: '-45px',
                }}
              >
                <ControllerElement
                  controllerId={controller?.id}
                  title={title ?? ''}
                  gatewayId={controller?.gatewayId}
                  isOpen={controllerState}
                  setControllerState={setControllerState}
                />
              </Box>
            )}
          </Box>
        </Box>
      </WidgetWrapper>
      <LiftStationWidgetPopup
        toggleAddOrRemoveHandler={toggleAddOrRemoveHandler}
        position={position}
        gatewayInputs={gatewayInputs}
        isOpen={state}
        onToggle={onToggle}
        isEdit={true}
        existingWidget={widget}
        selectedWidgetSubtype={widget?.widgetSubType!}
      />
    </>
  );
};
